import React, { useEffect, useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ClientDataTable from './components/ClientDataTable';
import StatusToggleModal from './components/StatusToggleModal';
import ActionConfirmModal from './components/ActionConfirmModal';

const AllClientsManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: 'createdDate',
    direction: 'desc'
  });
  const [statusToggleModal, setStatusToggleModal] = useState({
    isOpen: false,
    client: null,
    newStatus: null
  });
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    action: null,
    client: null
  });

  // DB data only
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiLoaded, setApiLoaded] = useState(false);

  const formatClientData = (client) => {
    return {
      id: client?._id || client?.id,
      ownerName: client?.fullName || '-',
      mobile: client?.mobileNumber || '-',
      email: client?.email || '-',
      companyName: client?.companyName || '-',
      businessType: client?.businessType
        ? client.businessType.charAt(0).toUpperCase() + client.businessType.slice(1)
        : '-',
      createdDate: client?.createdAt
        ? new Date(client.createdAt).toLocaleDateString('en-CA')
        : '-',
      lastActiveDate: client?.lastActiveDate
        ? new Date(client.lastActiveDate).toLocaleDateString('en-CA')
        : '-',
      status: client?.status || 'active',
      raw: client
    };
  };

  const fetchClients = async () => {
    try {
      setLoading(true);

      const response = await fetch('http://localhost:5000/api/business/all');
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || 'Failed to fetch clients');
      }

      if (Array.isArray(result?.data)) {
        const formattedClients = result.data.map(formatClientData);
        setClients(formattedClients);
      } else {
        setClients([]);
      }

      setApiLoaded(true);
    } catch (error) {
      console.error('Fetch clients error:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredClients = clients.filter((client) => {
    const query = searchQuery.toLowerCase();

    return (
      client?.ownerName?.toLowerCase()?.includes(query) ||
      client?.companyName?.toLowerCase()?.includes(query) ||
      client?.email?.toLowerCase()?.includes(query) ||
      client?.mobile?.toLowerCase()?.includes(query)
    );
  });

  const sortedClients = [...filteredClients].sort((a, b) => {
    if (sortConfig?.key) {
      const aValue = a?.[sortConfig?.key] || '';
      const bValue = b?.[sortConfig?.key] || '';

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const itemsPerPage = 10;
  const totalPages = Math.ceil(sortedClients.length / itemsPerPage) || 1;
  const paginatedClients = sortedClients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig?.key === key && sortConfig?.direction === 'asc'
          ? 'desc'
          : 'asc'
    });
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClients.length === paginatedClients.length && paginatedClients.length > 0) {
      setSelectedClients([]);
    } else {
      setSelectedClients(paginatedClients.map((c) => c.id));
    }
  };

  const handleStatusToggle = (client, newStatus) => {
    setStatusToggleModal({ isOpen: true, client, newStatus });
  };

  const confirmStatusToggle = async () => {
    try {
      if (!apiLoaded || !statusToggleModal?.client?.id) return;

      const response = await fetch(
        `http://localhost:5000/api/business/${statusToggleModal?.client?.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: statusToggleModal?.newStatus
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || 'Failed to update status');
      }

      await fetchClients();
      setStatusToggleModal({ isOpen: false, client: null, newStatus: null });
    } catch (error) {
      console.error('Status update error:', error);
      alert(error.message || 'Failed to update status');
    }
  };

  const handleAction = (action, client) => {
    setActionModal({ isOpen: true, action, client });
  };

  const confirmAction = async () => {
    const { action, client } = actionModal;

    try {
      if (!apiLoaded || !client?.id) return;

      if (action === 'delete') {
        const response = await fetch(`http://localhost:5000/api/business/${client?.id}`, {
          method: 'DELETE'
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.message || 'Failed to delete client');
        }
      }

      await fetchClients();
      setActionModal({ isOpen: false, action: null, client: null });
    } catch (error) {
      console.error('Client action error:', error);
      alert(error.message || 'Action failed');
    }
  };

  return (
    <div className="flex min-h-screen bg-background overflow-x-hidden">
      <AdminSidebar isCollapsed={isSidebarCollapsed} />

      <div
        className={`flex-1 min-w-0 flex flex-col overflow-hidden transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <AuthHeader
          isAuthenticated={true}
          userName="Super Admin"
          userSubscription="admin"
          onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-6 pt-20 md:pt-24">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading">
                  All Clients Management
                </h1>
                <p className="text-sm text-muted-foreground font-caption mt-1">
                  Client data from MongoDB only
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => console.log('Export data')}
                >
                  Export
                </Button>
              </div>
            </div>

            {loading && (
              <div className="bg-card border border-border rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Loading clients...</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption">Total Clients</p>
                    <p className="text-2xl font-bold text-foreground font-heading mt-1">
                      {clients.length}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="Users" size={20} color="var(--color-primary)" />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption">Active Clients</p>
                    <p className="text-2xl font-bold text-foreground font-heading mt-1">
                      {clients.filter((c) => c?.status === 'active').length}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption">Inactive Clients</p>
                    <p className="text-2xl font-bold text-foreground font-heading mt-1">
                      {clients.filter((c) => c?.status === 'inactive').length}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                    <Icon name="XCircle" size={20} color="var(--color-destructive)" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 elevation-sm overflow-hidden">
              <div className="relative">
                <Icon
                  name="Search"
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search by name, company, email, or mobile..."
                  className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
                />
              </div>
            </div>

            <div className="w-full overflow-x-auto rounded-lg">
              <div className="min-w-[1100px]">
                <ClientDataTable
                  clients={paginatedClients}
                  selectedClients={selectedClients}
                  onSelectClient={handleSelectClient}
                  onSelectAll={handleSelectAll}
                  onStatusToggle={handleStatusToggle}
                  onAction={handleAction}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-card border border-border rounded-lg p-4">
                <div className="text-sm text-muted-foreground font-caption">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, sortedClients.length)} of{' '}
                  {sortedClients.length} clients
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ChevronLeft"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  />
                  {[...Array(totalPages)].map((_, i) => (
                    <Button
                      key={i + 1}
                      variant={currentPage === i + 1 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ChevronRight"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <StatusToggleModal
        isOpen={statusToggleModal?.isOpen}
        client={statusToggleModal?.client}
        newStatus={statusToggleModal?.newStatus}
        onClose={() =>
          setStatusToggleModal({ isOpen: false, client: null, newStatus: null })
        }
        onConfirm={confirmStatusToggle}
      />

      <ActionConfirmModal
        isOpen={actionModal?.isOpen}
        action={actionModal?.action}
        client={actionModal?.client}
        onClose={() => setActionModal({ isOpen: false, action: null, client: null })}
        onConfirm={confirmAction}
      />
    </div>
  );
};

export default AllClientsManagement;